<script setup lang="ts">
import {
  ArrowLeftIcon,
  InfoIcon,
  MessageCircleIcon,
  PaperclipIcon,
} from "lucide-vue-next";

import MentionText from "@/components/mention-text.vue";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DiscussionThreadTags from "@/features/discussion-threads/components/discussion-thread-tags.vue";
import { formatByteSize } from "@/features/discussion-threads/formatters";
import type { DiscussionThreadAttachment } from "@/features/discussion-threads/types";
import type { DiscussionThread, MentionableEntity } from "@/types";

interface Props {
  thread: DiscussionThread;
  title: string;
  description: string;
  attachments: DiscussionThreadAttachment[];
  mentionables: MentionableEntity[];
  availableTags: string[];
  organizationId: number;
  isMobileViewport: boolean;
  showDetailButton: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: "back"): void;
  (e: "open-detail"): void;
}>();
</script>

<template>
  <div class="rounded-2xl border border-border bg-background">
    <div class="flex items-center gap-3 px-4 py-3 md:px-6 md:py-4">
      <button
        v-if="isMobileViewport"
        aria-label="Volver a hilos"
        class="-ml-1 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground"
        type="button"
        @click="emit('back')"
      >
        <ArrowLeftIcon class="h-5 w-5" />
      </button>

      <div
        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted"
      >
        <MessageCircleIcon class="h-4 w-4 text-muted-foreground" />
      </div>

      <h1
        class="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight text-foreground"
      >
        {{ title }}
      </h1>

      <button
        v-if="showDetailButton"
        aria-label="Ver detalles del hilo"
        class="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground"
        type="button"
        @click="emit('open-detail')"
      >
        <InfoIcon class="h-[18px] w-[18px]" />
      </button>
    </div>

    <Accordion type="single" collapsible class="w-full">
      <AccordionItem value="thread-details" class="border-none">
        <AccordionTrigger
          class="px-4 py-2 text-[12px] text-muted-foreground hover:no-underline md:px-6"
        >
          <span class="flex items-center gap-1.5">
            Ver descripción
            <span
              v-if="attachments.length > 0 || thread.tags.length > 0"
              class="text-muted-foreground/50"
            >
              ·
              <span
                v-if="attachments.length > 0"
                class="inline-flex items-center gap-0.5"
              >
                <PaperclipIcon class="inline h-3 w-3" />
                {{ attachments.length }}
              </span>
              <span v-if="thread.tags.length > 0">
                {{ attachments.length > 0 ? ", " : "" }}{{ thread.tags.length }}
                {{ thread.tags.length === 1 ? "tema" : "temas" }}
              </span>
            </span>
          </span>
        </AccordionTrigger>

        <AccordionContent class="px-4 pb-4 md:px-6">
          <div class="space-y-2.5 md:ml-11">
            <p class="text-[13px] leading-relaxed text-muted-foreground">
              <MentionText :text="description" :mentionables="mentionables" />
            </p>

            <div
              v-if="attachments.length > 0"
              class="border-t border-border pt-2.5"
            >
              <p class="mb-1.5 text-[11px] font-medium text-muted-foreground/60">
                Documentos ({{ attachments.length }})
              </p>
              <div class="max-h-40 space-y-1.5 overflow-y-auto pr-1">
                <a
                  v-for="(attachment, idx) in attachments"
                  :key="`inline-attachment-${idx}-${attachment.url}`"
                  :href="attachment.url"
                  download
                  class="flex items-center gap-2 rounded-lg border border-border/60 px-2.5 py-2 text-left transition hover:bg-muted/40"
                >
                  <PaperclipIcon
                    class="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground"
                  />
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-[12px] text-foreground">
                      {{ attachment.filename }}
                    </p>
                    <p class="text-[10px] text-muted-foreground/70">
                      {{ formatByteSize(attachment.byte_size) }}
                    </p>
                  </div>
                </a>
              </div>
            </div>

            <div class="border-t border-border pt-2.5">
              <DiscussionThreadTags
                :tags="thread.tags"
                :available-tags="availableTags"
                :organization-id="organizationId"
                :thread-id="thread.id"
                :can-edit="thread.can_update"
                variant="compact"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
</template>
